import { TradingAccountDetails } from '@/common/types'
import type { PostalAddress } from '@six-invest/api-types/shared/postal-address'
import { getAddressString } from '@/common/utils/utils'
import { NAVIGATION_COPY } from '@/locales/en'
import type { AccountSettingsDetails, ActionableDetailItem } from '../types'

const EMPTY_VALUE = '-'

const buildTradeAccountSettings = (tradingAccount: TradingAccountDetails | undefined): AccountSettingsDetails => {
    const copy = NAVIGATION_COPY.account.accountSettingsFields

    const clientEmailList =
        tradingAccount?.contractNoteEmails?.filter((email) => email.type === 'CLIENT').map((email) => email.email) || []
    const clientEmails = clientEmailList.length > 0 ? clientEmailList : EMPTY_VALUE

    const adviserEmailList =
        tradingAccount?.contractNoteEmails?.filter((email) => email.type === 'ADVISER').map((email) => email.email) ||
        []
    const adviserEmails = adviserEmailList.length > 0 ? adviserEmailList : EMPTY_VALUE

    const tradeConfirmation: ActionableDetailItem[] = [
        {
            key: 'tradeConfirmationEmail',
            label: copy.tradeConfirmationEmail,
            value: clientEmails,
        },
        ...(Array.isArray(adviserEmails) && adviserEmails.length > 0
            ? [
                  {
                      key: 'adviserTradeConfirmationEmail',
                      label: copy.adviserTradeConfirmationEmail,
                      value: adviserEmails,
                  },
              ]
            : []),
    ]

    const registrationAddress = tradingAccount?.registrationAddress
        ? getAddressString(tradingAccount.registrationAddress)
        : EMPTY_VALUE

    const registryCommunication: ActionableDetailItem[] = [
        {
            key: 'registrationAddress',
            label: copy.registrationAddress,
            value: registrationAddress,
        },
    ]

    return {
        tradeConfirmation,
        registryCommunication,
    }
}

const buildAdviserAccountSettings = (
    tradeContractEmail: string | undefined,
    tradeRegistrationAddress: PostalAddress | undefined,
    onAddAdviserTradeConfirmation?: () => void,
    onAddAdviserRegistrationAddress?: () => void,
): AccountSettingsDetails => {
    const copy = NAVIGATION_COPY.account.accountSettingsFields

    const tradeConfirmation: ActionableDetailItem[] = [
        {
            key: 'tradeConfirmationEmail',
            label: copy.tradeConfirmationEmail,
            value: tradeContractEmail || EMPTY_VALUE,
            ...(!tradeContractEmail && {
                onAdd: onAddAdviserTradeConfirmation,
            }),
        },
    ]

    const registrationAddress = tradeRegistrationAddress ? getAddressString(tradeRegistrationAddress) : EMPTY_VALUE

    const registryCommunication: ActionableDetailItem[] = [
        {
            key: 'registrationAddress',
            label: copy.registrationAddress,
            value: registrationAddress,
            ...(!tradeRegistrationAddress && {
                onAdd: onAddAdviserRegistrationAddress,
            }),
        },
    ]

    return {
        tradeConfirmation,
        registryCommunication,
    }
}

export const buildAccountSettingsDetailItems = (
    isAdviser: boolean,
    tradingAccount?: TradingAccountDetails | undefined,
    tradeContractEmail?: string,
    tradeRegistrationAddress?: PostalAddress,
    onAddAdviserTradeConfirmation?: () => void,
    onAddAdviserRegistrationAddress?: () => void,
): AccountSettingsDetails => {
    if (isAdviser) {
        return buildAdviserAccountSettings(
            tradeContractEmail,
            tradeRegistrationAddress,
            onAddAdviserTradeConfirmation,
            onAddAdviserRegistrationAddress,
        )
    }

    return buildTradeAccountSettings(tradingAccount)
}
